import { getCompiledFrameworkTargetContextLabel, getCompiledProjectTreeFolderManagedPlacement, getFrameworkTargetContextMetadataKey, } from "../framework-targets.js";
const MANAGED_PROJECT_TREE_FOLDER_BINDING_CONFIG = {
    systemKeyPayloadField: "projectTreeFolderSystemKey",
    parentFrameworkNodeKeyPayloadField: "projectTreeFolderParentFrameworkNodeKey",
    namePayloadField: "projectTreeFolderName",
};
function normalizeBindingParentTypeKey(parentTypeKey) {
    return parentTypeKey === "folder" ? "projectTreeFolder" : parentTypeKey;
}
function toParentBinding(rule) {
    if (rule.kind !== "contains") {
        return null;
    }
    const targetField = rule.createBinding?.targetField?.trim();
    if (!targetField) {
        return null;
    }
    return {
        parentEntityTypeKey: normalizeBindingParentTypeKey(rule.parent),
        persistenceKey: targetField,
        required: rule.childRequiresParent !== false,
        managedTargetConfig: rule.createBinding?.managedTarget === true
            ? MANAGED_PROJECT_TREE_FOLDER_BINDING_CONFIG
            : undefined,
    };
}
export function getEntityParentBindingsFromStructure(structure, entityTypeKey) {
    return (structure?.topologyRules ?? [])
        .filter((rule) => rule.child === entityTypeKey)
        .map(toParentBinding)
        .filter((binding) => binding !== null);
}
export function getEntityParentBindingPayloadDefaults(args) {
    return {
        binding: args.binding,
        payloadDefaults: {
            [args.binding.persistenceKey]: args.parentId,
        },
    };
}
export function getManagedEntityParentBindingDefaultsForBinding(args) {
    const managedTargetConfig = args.binding.managedTargetConfig;
    const placement = getCompiledProjectTreeFolderManagedPlacement({
        structure: args.structure,
        rootFolderKeyOrSlug: args.rootFolderSlug,
        targetEntityTypeKey: args.entityTypeKey,
    });
    if (!managedTargetConfig || !placement?.systemKey) {
        return null;
    }
    const payloadDefaults = {
        [managedTargetConfig.systemKeyPayloadField]: placement.systemKey,
    };
    if (placement.parentFrameworkNodeKey) {
        payloadDefaults[managedTargetConfig.parentFrameworkNodeKeyPayloadField] =
            placement.parentFrameworkNodeKey;
    }
    if (placement.name) {
        payloadDefaults[managedTargetConfig.namePayloadField] = placement.name;
    }
    return {
        binding: args.binding,
        systemKey: placement.systemKey,
        managedPayloadKeys: [
            managedTargetConfig.systemKeyPayloadField,
            managedTargetConfig.parentFrameworkNodeKeyPayloadField,
            managedTargetConfig.namePayloadField,
        ],
        payloadDefaults,
    };
}
export function getManagedEntityParentBindingDefaults(args) {
    for (const binding of getEntityParentBindingsFromStructure(args.structure, args.entityTypeKey)) {
        const defaults = getManagedEntityParentBindingDefaultsForBinding({
            binding,
            entityTypeKey: args.entityTypeKey,
            rootFolderSlug: args.rootFolderSlug,
            structure: args.structure,
        });
        if (defaults) {
            return defaults;
        }
    }
    return null;
}
export function getManagedProjectTreeFolderSystemKey(args) {
    return (getManagedEntityParentBindingDefaults({
        entityTypeKey: args.entityTypeKey,
        rootFolderSlug: args.rootFolderSlug,
        structure: args.structure,
    })?.systemKey ?? null);
}
export function getEntityCitationParentContexts(entityTypeKey, options) {
    return getEntityParentBindingsFromStructure(options.structure, entityTypeKey).map((binding) => ({
        parentEntityTypeKey: binding.parentEntityTypeKey,
        parentSlugKey: getFrameworkTargetContextMetadataKey({
            targetKey: binding.parentEntityTypeKey,
            bindingPersistenceKey: binding.persistenceKey,
        }),
        scoreWeight: binding.scoreWeight ?? 1,
    }));
}
export function getEntityParentContextDefinitions(entityTypeKey, options) {
    return getEntityParentBindingsFromStructure(options.structure, entityTypeKey).map((binding) => ({
        parentEntityTypeKey: binding.parentEntityTypeKey,
        bindingPersistenceKey: binding.persistenceKey,
        parentMetadataKey: getFrameworkTargetContextMetadataKey({
            targetKey: binding.parentEntityTypeKey,
            bindingPersistenceKey: binding.persistenceKey,
        }),
        parentLabel: getCompiledFrameworkTargetContextLabel({
            targetKey: binding.parentEntityTypeKey,
            structure: options.structure,
        }),
        scoreWeight: binding.scoreWeight ?? 1,
    }));
}
